
import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, User, ArrowRight } from 'lucide-react';
import FadeIn from '../../../components/FadeIn';
import SEO from '../../../components/SEO';

const ProfessionalComm = () => {
    return (
        <div className="bg-white">
            <SEO
                title="Professional Communication Training | BPO Consultants"
                description="Polish your business persona. Master active listening, email etiquette, and executive presence on Zoom."
                keywords="Business Communication, Active Listening, Email Etiquette, Executive Presence, Professional Skills"
            />

            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=1200" alt="Professional Meeting" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                            <Briefcase size={14} className="inline mr-1" /> Module 4: Soft Skills
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Professional <br /> Communication
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            It's not just what you say. It's how you say it.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <FadeIn className="mb-16">
                        <h2 className="text-3xl font-bold text-[#20222D] mb-6">Polishing Your Brand</h2>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            In a remote-first world, your communication style is your brand. This module covers the essential soft skills needed to be taken seriously by senior executives.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    title: "Active Listening",
                                    desc: "The ability to listen to understand, not just to respond. Using pausing and confirmation statements."
                                },
                                {
                                    title: "Zoom/Video Presence",
                                    desc: "Lighting, camera angles, eye contact, and background management for professional video calls."
                                },
                                {
                                    title: "Business Writing",
                                    desc: "Writing concise, clear, and action-oriented emails. Removing 'fluff' words like 'just' and 'checking in'."
                                },
                                {
                                    title: "Cultural Nuance",
                                    desc: "Understanding communication styles across different regions (US vs UK vs APAC) for BPO clients."
                                }
                            ].map((item, i) => (
                                <div key={i} className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <div className="text-[#A2CB52] mb-4">
                                        <User size={28} />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#20222D] mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>

                    {/* SGE / FAQ Section */}
                    <FadeIn className="mb-16">
                        <h2 className="text-2xl font-bold text-[#20222D] mb-6">Why This Matters</h2>
                        <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                            <p className="text-blue-900 font-medium italic">
                                "85% of job success comes from having well-developed soft and people skills."
                            </p>
                            <p className="text-blue-700 text-sm mt-2">- Carnegie Institute of Technology</p>
                        </div>
                    </FadeIn>

                    <FadeIn className="bg-[#20222D] rounded-3xl p-12 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                        <h2 className="text-3xl font-bold text-white mb-6">Elevate Yourself</h2>
                        <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                            Start Module <ArrowRight className="ml-2" size={20} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
};

export default ProfessionalComm;
